import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../../components/UI/card';
import { Button } from '../../components/UI/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/UI/tabs';
import { Loader2, Plus, History, Download, Mail, ArrowLeft } from 'lucide-react';
import CallGenerator from '../../components/Payment/CallGenerator';
import CallHistory from '../../components/Payment/CallHistory';
import CallDetail from '../../components/Payment/CallDetail';
import { useToast } from '../../hooks/use-toast';

/**
 * PAGE: APPELS À COTISATION
 * 
 * Gestion des appels à cotisation pour les entreprises
 */

const ContributionCalls = ({ embedded = false, onClose }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('generate');
  const [enterprises, setEnterprises] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      const [enterprisesRes, callsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/enterprises/options`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setEnterprises(enterprisesRes.data.data || []);
      setCalls(callsRes.data.data || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de charger les données'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (formData) => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('accessToken');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Succès',
        description: 'Appel à cotisation généré avec succès'
      });

      // Recharger la liste
      await loadData();
      setActiveTab('history');
    } catch (error) {
      console.error('Erreur génération:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors de la génération'
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleView = (callId) => {
    const call = calls.find(c => c.id === callId);
    setSelectedCall(call);
  };

  const handleCloseDetail = () => {
    setSelectedCall(null);
  };

  const handleDownloadExcel = async (callId) => {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls/${callId}/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appel-cotisation-${callId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Succès',
        description: 'Fichier Excel téléchargé'
      });
    } catch (error) {
      console.error('Erreur téléchargement Excel:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Erreur lors du téléchargement'
      });
    }
  };

  const handleDownloadPDF = async (callId) => {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls/${callId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appel-cotisation-${callId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Succès',
        description: 'Fichier PDF téléchargé'
      });
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Erreur lors du téléchargement'
      });
    }
  };

  const handleSend = async (callId) => {
    try {
      const token = localStorage.getItem('accessToken');

      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls/${callId}/send`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Succès',
        description: 'Appel à cotisation envoyé par email'
      });

      // Recharger la liste
      await loadData();
    } catch (error) {
      console.error('Erreur envoi:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors de l\'envoi'
      });
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-16' : 'min-h-screen'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-6' : 'container mx-auto py-6 space-y-6'}>
      {/* Bouton retour (seulement hors modal) */}
      {!embedded && (
        <button
          onClick={() => navigate('/admin/payments/entreprises')}
          className="flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Retour aux paiements entreprises</span>
        </button>
      )}

      {/* En-tête (seulement hors modal) */}
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold">Appels à Cotisation</h1>
          <p className="text-muted-foreground mt-2">
            Générez et envoyez les appels à cotisation aux entreprises
          </p>
        </div>
      )}

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generate">
            <Plus className="w-4 h-4 mr-2" />
            Générer
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="w-4 h-4 mr-2" />
            Historique ({calls.length})
          </TabsTrigger>
        </TabsList>

        {/* Génération */}
        <TabsContent value="generate" className="space-y-6">
          <CallGenerator
            enterprises={enterprises}
            onGenerate={handleGenerate}
            generating={generating}
            existingCalls={calls}
          />
        </TabsContent>

        {/* Historique */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Historique des appels</CardTitle>
              <CardDescription>
                Liste de tous les appels à cotisation générés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CallHistory
                calls={calls}
                onView={handleView}
                onDownloadExcel={handleDownloadExcel}
                onDownloadPDF={handleDownloadPDF}
                onSend={handleSend}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total générés</p>
              <p className="text-2xl font-bold">{calls.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Envoyés</p>
              <p className="text-2xl font-bold text-green-600">
                {calls.filter(c => c.status === 'SENT').length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">En attente</p>
              <p className="text-2xl font-bold text-orange-600">
                {calls.filter(c => c.status !== 'SENT').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de détail */}
      {selectedCall && (
        <CallDetail
          call={selectedCall}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
};

export default ContributionCalls;
